import { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function RecuperacionDistribuidorPanel() {
  const importRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    installation_id: "",
    email: "",
    titular: "",
    rfc: "",
    uses_max: "5",
    valid_days: "3",
    buyer_id: "",
    motivo: "",
  });

  const loadRegistry = async () => {
    try {
      const { data } = await api.get("/distributor/password-recovery/registry");
      setItems(data?.items || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    loadRegistry();
  }, []);

  const onImportSolicitud = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/distributor/import-recuperacion-solicitud-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        installation_id: data.installation_id || f.installation_id,
        email: data.email || f.email,
        titular: data.titular || f.titular,
        rfc: data.rfc || f.rfc,
        buyer_id: data.buyer?.id || f.buyer_id,
        motivo: data.motivo || f.motivo,
      }));
      toast.success(
        data.buyer_created
          ? "Solicitud importada y comprador dado de alta en catálogo"
          : "Solicitud importada",
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al importar");
    } finally {
      setBusy(false);
    }
  };

  const emitRespuesta = async () => {
    if (!form.installation_id.trim() || !form.email.trim()) {
      toast.error("ID de instalación y correo son obligatorios");
      return;
    }
    setBusy(true);
    try {
      const body = {
        installation_id: form.installation_id.trim(),
        email: form.email.trim(),
        titular: form.titular.trim(),
        rfc: form.rfc.trim(),
        uses_max: Number(form.uses_max) || 5,
        valid_days: Number(form.valid_days) || 3,
        buyer_id: form.buyer_id.trim() || undefined,
        motivo: form.motivo.trim(),
      };
      const res = await api.post("/distributor/password-recovery/respuesta-pdf", body, {
        responseType: "blob",
        timeout: 120000,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `recuperacion-${form.installation_id.replace(/[^A-Za-z0-9-]/g, "")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PDF de respuesta generado y registrado en cartera de recuperaciones");
      loadRegistry();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo emitir el PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-sm border border-amber-200 bg-amber-50/40 p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-amber-950">Recuperación de acceso por extravío</h3>
          <p className="text-[11px] text-amber-900/90 mt-1 max-w-2xl leading-snug">
            Importe la solicitud del cliente, emita el PDF de respuesta con código temporal (5 accesos por defecto) y
            entrégueselo. Queda registrado en la cartera de recuperaciones emitidas.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={loadRegistry} disabled={busy}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar lista
        </Button>
      </div>

      <input ref={importRef} type="file" accept=".pdf" className="hidden" onChange={onImportSolicitud} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 border-amber-400"
        onClick={() => importRef.current?.click()}
        disabled={busy}
      >
        <Upload className="w-3.5 h-3.5 mr-1" /> Importar PDF solicitud del cliente
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px]">ID instalación (VF-…)</Label>
          <Input
            className="mt-0.5 h-8 text-xs font-mono"
            value={form.installation_id}
            onChange={(e) => setForm((f) => ({ ...f, installation_id: e.target.value.toUpperCase() }))}
          />
        </div>
        <div>
          <Label className="text-[10px]">Correo de acceso</Label>
          <Input
            className="mt-0.5 h-8 text-xs font-mono"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div>
          <Label className="text-[10px]">Titular / razón social</Label>
          <Input
            className="mt-0.5 h-8 text-xs"
            value={form.titular}
            onChange={(e) => setForm((f) => ({ ...f, titular: e.target.value }))}
          />
        </div>
        <div>
          <Label className="text-[10px]">RFC</Label>
          <Input
            className="mt-0.5 h-8 text-xs font-mono"
            value={form.rfc}
            onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value.toUpperCase() }))}
          />
        </div>
        <div>
          <Label className="text-[10px]">Accesos máximos</Label>
          <Input
            className="mt-0.5 h-8 text-xs"
            type="number"
            min={1}
            max={20}
            value={form.uses_max}
            onChange={(e) => setForm((f) => ({ ...f, uses_max: e.target.value }))}
          />
        </div>
        <div>
          <Label className="text-[10px]">Vigencia (días)</Label>
          <Input
            className="mt-0.5 h-8 text-xs"
            type="number"
            min={1}
            max={30}
            value={form.valid_days}
            onChange={(e) => setForm((f) => ({ ...f, valid_days: e.target.value }))}
          />
        </div>
      </div>

      <Button type="button" className="h-9 bg-amber-600 hover:bg-amber-700" onClick={emitRespuesta} disabled={busy}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
        Generar PDF de respuesta (código de acceso)
      </Button>

      {items.length > 0 && (
        <div className="border border-amber-100 rounded-sm bg-white overflow-x-auto max-h-48">
          <table className="w-full text-[10px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1">Fecha</th>
                <th className="text-left px-2 py-1">Instalación</th>
                <th className="text-left px-2 py-1">Correo</th>
                <th className="text-left px-2 py-1">Vence</th>
                <th className="text-center px-2 py-1">Usos</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-2 py-1 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-1 font-mono">{r.installation_id}</td>
                  <td className="px-2 py-1 font-mono">{r.email}</td>
                  <td className="px-2 py-1">{r.valid_until}</td>
                  <td className="px-2 py-1 text-center">{r.uses_max}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
